import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';

import { toErrorMessage } from '@util/toErrorMessage';
import {
  connectAppleMusic,
  disconnectAppleMusic,
  fetchAppleMusicConnection,
  fetchAppleMusicDeveloperToken,
  removeAppleMusicTrack,
  saveAppleMusicTrack,
} from '@util/appleMusicSession';

import { AppleMusicPlayerContext } from './appleMusicPlayerContext';
import {
  parseAppleMusicUri,
  playlistUri,
  songUri,
  trackFromNowPlayingItem,
} from './playerFormat';

// MusicKit v3. v1 attaches Widevine MediaKeys in Chrome but never performs
// license acquisition, so full tracks buffer to SourceBuffer overflow while the
// element sits at readyState 1. v3 is the build where Apple supports non-Safari
// full-track playback. v3 also drops `music.player` and flattens its members
// onto the instance — reads below keep a `music.player?.x` fallback so both
// shapes work.
const SDK_SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
const APP_NAME = "T&C's Gymnasium";
const APP_BUILD = '1.0.0';

function isLocalDevHost() {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

// Chrome drops sticky user activation across awaits. MusicKit's audio.play()
// catches rejections (NotAllowedError) and swallows them. Unlock with a
// SEPARATE element — never touch #apple-music-player (play-then-pause there
// races MusicKit's MediaSource blob and leaves paused:false / time:0 silence).
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

function unlockChromeAudioGesture() {
  try {
    const silent = new Audio(SILENT_WAV);
    silent.volume = 0.001;
    const playPromise = silent.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  } catch {
    // ignore — unlock is best-effort
  }
}

// v1 always used id="apple-music-player"; v3 does not guarantee that id, and a
// missed element makes every playback probe read as "not started". Fall back to
// any DOM <audio> — our silent unlock element is never appended, so it can't
// be picked up here.
function readAppleMusicAudioEl() {
  return (
    document.getElementById('apple-music-player') ||
    document.querySelector('audio')
  );
}

function readAudioBufferedSeconds(audio) {
  try {
    if (!audio?.buffered || audio.buffered.length < 1) return 0;
    return Number(audio.buffered.end(audio.buffered.length - 1)) || 0;
  } catch {
    return 0;
  }
}

function logAppleMusicAudioEl(label) {
  const audio = readAppleMusicAudioEl();
  if (!audio) {
    console.info(`[Apple Music] audio el (${label})`, { present: false });
    return null;
  }
  console.info(`[Apple Music] audio el (${label})`, {
    present: true,
    src: (audio.currentSrc || audio.src || '').slice(0, 120),
    paused: audio.paused,
    ended: audio.ended,
    muted: audio.muted,
    readyState: audio.readyState,
    networkState: audio.networkState,
    currentTime: audio.currentTime,
    bufferedEnd: readAudioBufferedSeconds(audio),
    hasMediaKeys: Boolean(audio.mediaKeys),
    error: audio.error
      ? { code: audio.error.code, message: audio.error.message }
      : null,
  });
  return audio;
}

function bindAppleMusicAudioDiagnostics(audio) {
  if (!audio || audio.dataset.amDiagBound === '1') return;
  audio.dataset.amDiagBound = '1';
  [
    'encrypted',
    'waitingforkey',
    'loadedmetadata',
    'canplay',
    'playing',
    'waiting',
    'stalled',
    'suspend',
    'error',
  ].forEach((type) => {
    audio.addEventListener(type, () => {
      console.info(`[Apple Music] audio:${type}`, {
        currentTime: audio.currentTime,
        readyState: audio.readyState,
        bufferedEnd: readAudioBufferedSeconds(audio),
        hasMediaKeys: Boolean(audio.mediaKeys),
        error: audio.error
          ? { code: audio.error.code, message: audio.error.message }
          : null,
      });
    });
  });
}

// Real playback only — !paused with currentTime stuck at 0 is a silent MSE stall.
function readHtmlAudioActive() {
  const audio = readAppleMusicAudioEl();
  if (!audio) return false;
  if (audio.error) return false;
  const src = audio.currentSrc || audio.src || '';
  if (!src) return false;
  if (audio.ended) return false;
  return Number(audio.currentTime) > 0.25;
}

function readHtmlAudioCurrentTime() {
  const audio = readAppleMusicAudioEl();
  if (!audio) return 0;
  return Number(audio.currentTime) || 0;
}

// v1's public player is SerialPlayback with DRM/audio on _currentPlayer. v3 has
// no `music.player` at all. Diagnostics only — returning null must stay harmless.
function getMusicKitAudioPlayer(music) {
  return (
    music?.player?._currentPlayer ||
    music?.player ||
    music?._playbackController ||
    music?._player ||
    null
  );
}

function getMusicKitExtension(music) {
  return getMusicKitAudioPlayer(music)?.extension || null;
}

// v1's MusicKitInstance does not proxy previewOnly, so it has to be set on the
// inner player. v3's PlaybackController exposes previewOnly as a getter only —
// assigning there throws in strict mode — while the v3 instance setter works
// (it maps to playbackMode). Try every target and keep whichever accepts it.
function setMusicKitPreviewOnly(music, value) {
  const targets = new Set([getMusicKitAudioPlayer(music), music?.player, music]);
  let applied = false;
  targets.forEach((target) => {
    if (!target) return;
    try {
      target.previewOnly = value;
      applied = true;
    } catch {
      // readonly on this target — fall through to the next one
    }
  });
  return applied;
}

function readMusicKitPreviewOnly(music) {
  const audioPlayer = getMusicKitAudioPlayer(music);
  return audioPlayer?.previewOnly ?? music?.player?.previewOnly ?? false;
}

async function waitForChromeMediaKeys(music, timeoutMs = 8000) {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) return true;
  // v3 sequences its own setMediaKeys and exposes no player.extension, so this
  // poll can only ever time out — and it did, twice, costing 16s per play and
  // risking the click's user activation. v1-only from here down.
  if (!music?.player) {
    bindAppleMusicAudioDiagnostics(readAppleMusicAudioEl());
    return true;
  }
  const deadline = Date.now() + timeoutMs;
  // Touch inner AudioPlayer so MediaExtension constructs and kicks Widevine attach.
  void getMusicKitAudioPlayer(music)?.extension;
  void music?.player?.canSupportDRM;
  while (Date.now() < deadline) {
    const audioPlayer = getMusicKitAudioPlayer(music);
    const audio =
      audioPlayer?.audio ||
      readAppleMusicAudioEl() ||
      document.getElementById('apple-music-player');
    const extension = audioPlayer?.extension;
    // Bind before any play attempt so we never miss `encrypted`.
    bindAppleMusicAudioDiagnostics(audio);
    if (audio?.mediaKeys) {
      console.info('[Apple Music] mediaKeys ready', {
        keySystem: extension?.session?.keySystem || null,
        isWidevine: extension?.session?.isWidevine,
        hasMediaSession: extension?.hasMediaSession,
      });
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  const extension = getMusicKitExtension(music);
  console.warn('[Apple Music] mediaKeys not ready before play', {
    hasPlayer: Boolean(music?.player),
    hasAudioPlayer: Boolean(getMusicKitAudioPlayer(music)),
    hasExtension: Boolean(extension),
    hasSession: Boolean(extension?.session),
    audioPresent: Boolean(readAppleMusicAudioEl()),
  });
  return false;
}

// The whole Chrome question is whether MusicKit ever asks for a license.
// generateRequest firing means it did; silence means it never tried.
function instrumentEmeOnce() {
  if (!import.meta.env?.DEV) return;
  if (window.__appleMusicEmeInstrumented) return;
  if (typeof window.MediaKeys === 'undefined') return;
  window.__appleMusicEmeInstrumented = true;

  const originalCreateSession = window.MediaKeys.prototype.createSession;
  window.MediaKeys.prototype.createSession = function (...args) {
    const session = originalCreateSession.apply(this, args);
    console.info('[Apple Music] EME createSession', { sessionType: args[0] });

    const originalGenerateRequest = session.generateRequest;
    session.generateRequest = function (initDataType, initData) {
      console.info('[Apple Music] EME generateRequest', {
        initDataType,
        initDataBytes: initData?.byteLength ?? null,
      });
      return originalGenerateRequest.call(this, initDataType, initData);
    };

    const originalUpdate = session.update;
    session.update = function (response) {
      console.info('[Apple Music] EME license update', {
        responseBytes: response?.byteLength ?? null,
      });
      return originalUpdate.call(this, response);
    };

    session.addEventListener('message', (event) => {
      console.info('[Apple Music] EME message (license request)', {
        messageType: event.messageType,
        messageBytes: event.message?.byteLength ?? null,
      });
    });
    session.addEventListener('keystatuseschange', () => {
      const statuses = [];
      session.keyStatuses?.forEach((status) => statuses.push(status));
      console.info('[Apple Music] EME keystatuseschange', { statuses });
    });
    return session;
  };
}

// MusicKit requests Widevine with this exact EME config.
// Probe is diagnostics only — never gate playback on it (music.apple.com can
// succeed in the same Chrome even when a premature probe looked "failed").
const MUSICKIT_WIDEVINE_AUDIO =
  'audio/mp4;codecs="mp4a.40.2"';
let widevinePersistentProbe = null;

function probeWidevinePersistentRequired() {
  if (widevinePersistentProbe) return widevinePersistentProbe;
  if (typeof navigator.requestMediaKeySystemAccess !== 'function') {
    widevinePersistentProbe = Promise.resolve({
      ok: false,
      reason: 'no-eme',
    });
    return widevinePersistentProbe;
  }
  const config = [
    {
      initDataTypes: ['cenc', 'keyids'],
      audioCapabilities: [{ contentType: MUSICKIT_WIDEVINE_AUDIO }],
      distinctiveIdentifier: 'optional',
      persistentState: 'required',
    },
  ];
  widevinePersistentProbe = navigator
    .requestMediaKeySystemAccess('com.widevine.alpha', config)
    .then(() => ({ ok: true }))
    .catch((error) => ({
      ok: false,
      reason: 'not-supported',
      error,
    }));
  return widevinePersistentProbe;
}

function loadMusicKitSdk() {
  if (window.MusicKit) {
    return Promise.resolve(window.MusicKit);
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_SRC}"]`);
    if (existing) {
      if (window.MusicKit) {
        resolve(window.MusicKit);
        return;
      }
      document.addEventListener(
        'musickitloaded',
        () => resolve(window.MusicKit),
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.onerror = () => reject(new Error('Failed to load Apple MusicKit'));
    document.addEventListener(
      'musickitloaded',
      () => resolve(window.MusicKit),
      { once: true },
    );
    document.body.appendChild(script);
  });
}

function musicQueue(music) {
  return music?.queue || music?.player?.queue || null;
}

async function musicPlay(music) {
  if (typeof music?.play === 'function') {
    return music.play();
  }
  if (typeof music?.player?.play === 'function') {
    return music.player.play();
  }
  throw new Error('MusicKit play() is not available');
}

function musicKitRepeatMode(music, state) {
  const modes = music?.RepeatMode || window.MusicKit?.RepeatMode || {};
  if (state === 'track') return modes.ONE ?? 1;
  if (state === 'context') return modes.ALL ?? 2;
  return modes.NONE ?? 0;
}

function repeatStateFromMode(mode) {
  if (mode === 1) return 'track';
  if (mode === 2) return 'context';
  return 'off';
}

function isPlayingState(music, state) {
  const playbackStates = music?.PlaybackStates || window.MusicKit?.PlaybackStates || {};
  if (playbackStates.playing != null && state === playbackStates.playing) {
    return true;
  }
  return state === 2 || state === 'playing' || state === 'PLAYING';
}

function isPausedOrStoppedState(music, state) {
  const playbackStates = music?.PlaybackStates || window.MusicKit?.PlaybackStates || {};
  if (playbackStates.paused != null && state === playbackStates.paused) {
    return true;
  }
  if (playbackStates.stopped != null && state === playbackStates.stopped) {
    return true;
  }
  return (
    state === 3 ||
    state === 4 ||
    state === 'paused' ||
    state === 'stopped' ||
    state === 'PAUSED' ||
    state === 'STOPPED'
  );
}

// Safari/Chrome MusicKit often leave playbackState at 0 (none) while audio plays.
// Never infer "playing" from a frozen currentPlaybackTime — that breaks pause UI.
function resolveDockIsPlaying(music, userIntent) {
  if (!music) return false;
  if (userIntent === 'paused') return false;
  if (userIntent === 'playing') {
    const state = music.playbackState ?? music.player?.playbackState;
    if (isPausedOrStoppedState(music, state)) return false;
    return true;
  }
  if (music.isPlaying === true || music.player?.isPlaying === true) return true;
  if (music.isPlaying === false && music.player?.isPlaying !== true) {
    if (readHtmlAudioActive()) return true;
    return false;
  }
  const state = music.playbackState ?? music.player?.playbackState;
  if (isPlayingState(music, state)) return true;
  if (isPausedOrStoppedState(music, state)) return false;
  if (readHtmlAudioActive()) return true;
  return false;
}

function readPlaybackActiveForProbe(music) {
  if (readHtmlAudioActive()) return true;
  if (!music) return false;
  if (music.isPlaying === true || music.player?.isPlaying === true) return true;
  const state = music.playbackState ?? music.player?.playbackState;
  if (isPlayingState(music, state)) return true;
  const time = Number(
    music.currentPlaybackTime ?? music.player?.currentPlaybackTime ?? 0,
  );
  if (time > 0.25) return true;
  return readHtmlAudioCurrentTime() > 0.25;
}

function readPlaybackState(music) {
  return music?.playbackState ?? music?.player?.playbackState;
}

// mediaPlaybackError also carries errors that never affected audio:
// AbortError from our own overlapping play()/load, and PLAY_ACTIVITY bookkeeping
// that desyncs when a track is skipped mid-start. Neither is user-facing.
function isBenignPlaybackError(raw, message) {
  if (raw?.name === 'AbortError') return true;
  const text = String(message);
  if (/AbortError|interrupted by a new load/i.test(text)) return true;
  if (/PLAY_ACTIVITY|without a previous play\(\) descriptor/i.test(text)) {
    return true;
  }
  return false;
}

export function AppleMusicPlayerProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const [connection, setConnection] = useState(null);
  const [ready, setReady] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [playback, setPlayback] = useState({
    is_playing: false,
    progress_ms: 0,
    shuffle_state: false,
    repeat_state: 'off',
    track: null,
    device: null,
    context_uri: null,
  });
  const [playerError, setPlayerError] = useState(null);
  const [nowPlayingOpen, setNowPlayingOpen] = useState(false);
  const [headerPlayerMounted, setHeaderPlayerMounted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [volume, setVolumeState] = useState(0.85);
  const musicRef = useRef(null);
  const configuredTokenRef = useRef(null);
  const configuredExpiresAtRef = useRef(null);
  const listenersBoundRef = useRef(false);
  const localPreviewNoticeShownRef = useRef(false);
  const userPlaybackIntentRef = useRef('stopped');

  const registerHeaderPlayer = useCallback(() => {
    setHeaderPlayerMounted(true);
    return () => setHeaderPlayerMounted(false);
  }, []);

  const handlePlayerError = useCallback((error) => {
    const message = toErrorMessage(error);
    setPlayerError(message);
    if (message) {
      toast.error(message);
    }
  }, []);

  const syncFromMusic = useCallback((music) => {
    if (!music) return;
    const state = readPlaybackState(music);
    if (isPlayingState(music, state) || readHtmlAudioActive()) {
      userPlaybackIntentRef.current = 'playing';
    } else if (isPausedOrStoppedState(music, state)) {
      userPlaybackIntentRef.current = 'paused';
    }
    const item = music.nowPlayingItem || music.player?.nowPlayingItem;
    const mkTime = Number(
      music.currentPlaybackTime ?? music.player?.currentPlaybackTime ?? 0,
    );
    const audioTime = readHtmlAudioCurrentTime();
    const progressMs = Math.round(Math.max(mkTime, audioTime) * 1000);
    setPlayback((prev) => ({
      ...prev,
      is_playing: resolveDockIsPlaying(
        music,
        userPlaybackIntentRef.current,
      ),
      progress_ms: progressMs,
      shuffle_state: Boolean(music.shuffleMode ?? music.player?.shuffleMode),
      repeat_state: repeatStateFromMode(
        music.repeatMode ?? music.player?.repeatMode,
      ),
      track: trackFromNowPlayingItem(item, prev.track),
      context_uri: prev.context_uri,
    }));
  }, []);

  const progressPollRef = useRef(null);

  const stopProgressPoll = useCallback(() => {
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
      progressPollRef.current = null;
    }
  }, []);

  const startProgressPoll = useCallback(() => {
    stopProgressPoll();
    progressPollRef.current = setInterval(() => {
      const music = musicRef.current;
      if (!music) return;
      syncFromMusic(music);
      if (userPlaybackIntentRef.current !== 'playing') {
        stopProgressPoll();
      }
    }, 500);
  }, [stopProgressPoll, syncFromMusic]);

  useEffect(() => () => stopProgressPoll(), [stopProgressPoll]);

  const bindMusicListeners = useCallback(
    (music) => {
      if (!music || listenersBoundRef.current) return;
      listenersBoundRef.current = true;

      const onSync = () => syncFromMusic(music);
      const onTime = () => {
        const mkTime = Number(
          music.currentPlaybackTime ?? music.player?.currentPlaybackTime ?? 0,
        );
        const time = Math.max(mkTime, readHtmlAudioCurrentTime());
        setPlayback((prev) => ({
          ...prev,
          progress_ms: Math.round(time * 1000),
          is_playing: resolveDockIsPlaying(
            music,
            userPlaybackIntentRef.current,
          ),
          track:
            trackFromNowPlayingItem(
              music.nowPlayingItem || music.player?.nowPlayingItem,
              prev.track,
            ) || prev.track,
        }));
      };

      music.addEventListener('nowPlayingItemDidChange', onSync);
      music.addEventListener('playbackStateDidChange', onSync);
      music.addEventListener('playbackTimeDidChange', onTime);
      if (music.player && typeof music.player.addEventListener === 'function') {
        music.player.addEventListener('nowPlayingItemDidChange', onSync);
        music.player.addEventListener('playbackStateDidChange', onSync);
        music.player.addEventListener('playbackTimeDidChange', onTime);
      }
      music.addEventListener('mediaPlaybackError', (event) => {
        const raw =
          event?.error ||
          event?.message ||
          event;
        const message =
          (typeof raw === 'string' ? raw : raw?.message) ||
          'Apple Music media playback error';
        if (isBenignPlaybackError(raw, message)) {
          console.warn('[Apple Music] mediaPlaybackError (ignored)', event);
          return;
        }
        console.error('[Apple Music] mediaPlaybackError', event);
        setPlayerError(message);
        toast.error(message);
      });

      // v1 hid license failures on the inner AudioPlayer's extension instead of
      // surfacing them as mediaPlaybackError. v3 has no extension and reports
      // them through mediaPlaybackError above, so its absence is expected.
      const extension = getMusicKitExtension(music);
      if (extension && typeof extension.addEventListener === 'function') {
        extension.addEventListener('playbackLicenseError', (event) => {
          console.error('[Apple Music] playbackLicenseError', event);
        });
        extension.addEventListener('playbackSessionError', (event) => {
          console.error('[Apple Music] playbackSessionError', event);
        });
      }
    },
    [syncFromMusic],
  );

  const ensureConfigured = useCallback(async () => {
    const MusicKit = await loadMusicKitSdk();
    const expiresAtMs = configuredExpiresAtRef.current
      ? Date.parse(configuredExpiresAtRef.current)
      : 0;
    const tokenStillFresh =
      Number.isFinite(expiresAtMs) && expiresAtMs > Date.now() + 60_000;

    // Do not re-mint/reconfigure on every play — a new JWT string would
    // reconfigure MusicKit and drop the authorized music-user-token.
    if (musicRef.current && configuredTokenRef.current && tokenStillFresh) {
      const music = musicRef.current;
      setAuthorized(Boolean(music?.isAuthorized));
      setReady(Boolean(music));
      return music;
    }

    const tokenPayload = await fetchAppleMusicDeveloperToken();
    const developerToken = tokenPayload?.developer_token;
    if (!developerToken) {
      throw new Error('Missing Apple Music developer token');
    }

    // v3 resolves configure() with the instance; v1 resolves undefined.
    const configured = await MusicKit.configure({
      developerToken,
      app: {
        name: APP_NAME,
        build: APP_BUILD,
      },
    });
    configuredTokenRef.current = developerToken;
    configuredExpiresAtRef.current = tokenPayload?.expires_at || null;
    musicRef.current = configured || MusicKit.getInstance();
    if (typeof musicRef.current.volume === 'number') {
      musicRef.current.volume = volume;
    }
    listenersBoundRef.current = false;
    bindMusicListeners(musicRef.current);

    const music = musicRef.current;
    instrumentEmeOnce();
    probeWidevinePersistentRequired();
    void waitForChromeMediaKeys(music);
    console.info('[Apple Music] configured', {
      authorized: Boolean(music?.isAuthorized),
      hasPlayer: Boolean(music?.player),
      hasSetQueue: typeof music?.setQueue,
      hasPlay: typeof music?.play,
      hasChangeToMediaAtIndex: typeof music?.changeToMediaAtIndex,
      pageOrigin: window.location.origin,
      sdk: SDK_SRC,
      version: window.MusicKit?.version,
    });
    setAuthorized(Boolean(music?.isAuthorized));
    setReady(Boolean(music));
    return music;
  }, [bindMusicListeners, volume]);

  const refreshConnection = useCallback(() => {
    return fetchAppleMusicConnection()
      .then((payload) => {
        const isConnected = Boolean(payload?.connected);
        setConnected(isConnected);
        setConnection(payload || { connected: false });
        return payload;
      })
      .catch((error) => {
        setConnected(false);
        setConnection({ connected: false });
        throw error;
      });
  }, []);

  const authorizeAndConnect = useCallback(async () => {
    setBusy(true);
    setPlayerError(null);
    try {
      const music = await ensureConfigured();
      const musicUserToken = await music.authorize();
      const token =
        musicUserToken || music.musicUserToken || null;
      if (!token) {
        throw new Error('Apple Music authorization did not return a user token');
      }
      console.info('[Apple Music] authorized', {
        hasPlayer: Boolean(music.player),
        hasLibrary: Boolean(music.api?.library),
        version: window.MusicKit?.version,
        pageOrigin: window.location.origin,
      });
      const payload = await connectAppleMusic({ music_user_token: token });
      setConnected(Boolean(payload?.connected));
      setConnection(payload || { connected: true });
      setAuthorized(true);
      setReady(true);
      setPlayerError(null);
      return payload;
    } catch (error) {
      handlePlayerError(error);
      throw error;
    } finally {
      setBusy(false);
    }
  }, [ensureConfigured, handlePlayerError]);

  const disconnectAndForget = useCallback(async () => {
    setBusy(true);
    setPlayerError(null);
    try {
      const music = musicRef.current;
      if (music) {
        try {
          if (typeof music.stop === 'function') {
            await music.stop();
          } else if (typeof music.pause === 'function') {
            await music.pause();
          }
        } catch {
          // Best-effort stop before clearing auth.
        }
        if (typeof music.unauthorize === 'function') {
          await music.unauthorize();
        }
      }
      const payload = await disconnectAppleMusic();
      setConnected(false);
      setConnection(payload || { connected: false });
      setAuthorized(false);
      setReady(false);
      return payload;
    } catch (error) {
      handlePlayerError(error);
      throw error;
    } finally {
      setBusy(false);
    }
  }, [handlePlayerError]);

  const runControl = useCallback(
    (fn) => {
      setBusy(true);
      setPlayerError(null);
      return Promise.resolve()
        .then(fn)
        .then((result) => {
          syncFromMusic(musicRef.current);
          return result;
        })
        .catch((error) => {
          handlePlayerError(error);
          throw error;
        })
        .finally(() => setBusy(false));
    },
    [handlePlayerError, syncFromMusic],
  );

  const ensureAuthorizedMusic = useCallback(async () => {
    // Prefer the already-configured instance so play stays in the user-gesture
    // turn as much as possible (autoplay policies).
    let music = musicRef.current;
    if (!music) {
      music = await ensureConfigured();
    }
    if (!music?.isAuthorized) {
      throw new Error(
        'Authorize Apple Music on this browser first (Enable Apple Music).',
      );
    }
    return music;
  }, [ensureConfigured]);

  const playContext = useCallback(
    ({ contextUri, uris, offsetUri, externalUrl } = {}) => {
      // Must run in the same synchronous turn as the click (before awaits),
      // or Chrome will reject MusicKit's later audio.play() as NotAllowedError.
      unlockChromeAudioGesture();
      return runControl(async () => {
        const music = await ensureAuthorizedMusic();
        const storefront =
          music.storefrontId || connection?.storefront || 'us';

        let kind = null;
        let mediaId = null;
        if (contextUri) {
          const parsed = parseAppleMusicUri(contextUri);
          kind = parsed?.kind || null;
          mediaId = parsed?.id || null;
        } else if (uris?.length) {
          const parsed = parseAppleMusicUri(uris[0]);
          kind = parsed?.kind || null;
          mediaId = parsed?.id || null;
        }

        if (!kind || !mediaId) {
          throw new Error('Missing Apple Music queue item');
        }

        if (typeof music.volume === 'number') {
          music.volume = volume;
        }
        if (!music.storefrontId && storefront) {
          music.storefrontId = storefront;
        }

        console.info('[Apple Music] play diagnostics', {
          kind,
          mediaId,
          storefront,
          authorized: music.isAuthorized,
          hasMusicUserToken: Boolean(music.musicUserToken),
          hasPlayer: Boolean(music.player),
          hasSetQueue: typeof music.setQueue,
          hasPlay: typeof music.play,
          pageOrigin: window.location.origin,
          version: window.MusicKit?.version,
        });

        async function queueHasItems() {
          const queue = musicQueue(music);
          if (!queue) return false;
          if (queue.isEmpty === true) return false;
          const itemCount = Array.isArray(queue.items)
            ? queue.items.length
            : Number(queue.length) || 0;
          if (itemCount > 0) return true;
          return Boolean(music.nowPlayingItem || music.player?.nowPlayingItem);
        }

        async function trySetQueue(label, options) {
          console.info(`[Apple Music] setQueue (${label})`, options);
          try {
            const returned = await music.setQueue(options);
            const queue = musicQueue(music);
            console.info(`[Apple Music] queue result (${label})`, {
              returnedConstructor: returned?.constructor?.name,
              returnedLength: returned?.items?.length ?? returned?.length,
              queueLength: queue?.items?.length ?? queue?.length,
              isEmpty: queue?.isEmpty,
              queuePosition: queue?.position,
              nextPlayableItemIndex: queue?.nextPlayableItemIndex,
              nowPlaying:
                music.nowPlayingItem?.attributes?.name ||
                music.nowPlayingItem?.id ||
                music.player?.nowPlayingItem?.attributes?.name,
              playbackState: readPlaybackState(music),
            });
            return queueHasItems();
          } catch (error) {
            console.error(`[Apple Music] setQueue threw (${label})`, error);
            return false;
          }
        }

        function withTimeout(promise, ms, label) {
          let timer;
          const timeout = new Promise((resolve) => {
            timer = setTimeout(() => {
              console.warn(`[Apple Music] ${label} timed out after ${ms}ms`);
              resolve({ timedOut: true });
            }, ms);
          });
          return Promise.race([
            Promise.resolve(promise)
              .then((value) => ({ timedOut: false, value }))
              .catch((error) => ({ timedOut: false, error })),
            timeout,
          ]).finally(() => clearTimeout(timer));
        }

        async function pollUntilPlaying(label, attempts = 50) {
          for (let i = 0; i < attempts; i += 1) {
            const state = readPlaybackState(music);
            const mkTime =
              music.currentPlaybackTime ?? music.player?.currentPlaybackTime ?? 0;
            const audioTime = readHtmlAudioCurrentTime();
            const htmlActive = readHtmlAudioActive();
            if (
              isPlayingState(music, state) ||
              readPlaybackActiveForProbe(music) ||
              htmlActive
            ) {
              console.info(`[Apple Music] playback active (${label})`, {
                state,
                mkTime,
                audioTime,
                htmlActive,
                audioPaused: readAppleMusicAudioEl()?.paused,
              });
              return true;
            }
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
          return false;
        }

        async function startPlaybackOnce(label) {
          const widevine = await probeWidevinePersistentRequired();
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent,
          );
          if (!window.__appleMusicEmeLogged) {
            window.__appleMusicEmeLogged = true;
            const audioPlayer = getMusicKitAudioPlayer(music);
            const extension = audioPlayer?.extension;
            console.info('[Apple Music] media capabilities', {
              eme: typeof navigator.requestMediaKeySystemAccess === 'function',
              canSupportDRM: music.player?.canSupportDRM,
              hasMediaSession: extension?.hasMediaSession,
              isFairplay: extension?.isFairplay,
              isWidevine: extension?.session?.isWidevine,
              keySystem: extension?.session?.keySystem || null,
              hasMediaKeys: Boolean(audioPlayer?.audio?.mediaKeys),
              widevinePersistentRequired: widevine.ok,
              widevineFailReason: widevine.reason || null,
              widevineAudio: MUSICKIT_WIDEVINE_AUDIO,
              pageOrigin: window.location.origin,
              version: window.MusicKit?.version,
              isSafari,
            });
            if (!widevine.ok && !isSafari) {
              console.warn(
                '[Apple Music] Widevine probe failed (diagnostic only — still attempting MusicKit DRM play)',
                widevine.error || widevine.reason,
              );
            } else if (!widevine.ok && isSafari) {
              console.info(
                '[Apple Music] Widevine unavailable (expected on Safari — FairPlay is used instead)',
              );
            }
          }

          const queue = musicQueue(music);
          const queueItems = queue?.items || [];
          const position = Number(queue?.position);
          // MusicKit.play() uses nextPlayableItemIndex = position+1. After
          // setQueue with one song, position is often already 0 so play() is a
          // no-op. Start the current (or first) index via changeToMediaAtIndex.
          let startIndex = 0;
          if (Number.isFinite(position) && position >= 0 && queueItems[position]) {
            startIndex = position;
          } else {
            const playable = queueItems.findIndex((entry) => entry?.isPlayable !== false);
            startIndex = playable >= 0 ? playable : 0;
          }

          const item =
            queueItems[startIndex] ||
            music.nowPlayingItem ||
            music.player?.nowPlayingItem ||
            null;

          const before = readPlaybackState(music);
          const hasPlayerNowPlaying = Boolean(music.player?.nowPlayingItem);
          console.info(`[Apple Music] startPlayback (${label})`, {
            playbackState: before,
            nowPlaying: item?.attributes?.name || item?.id || null,
            hasPlayerNowPlaying,
            queuePosition: position,
            startIndex,
            nextPlayableItemIndex: queue?.nextPlayableItemIndex,
            isPlayable: Boolean(item?.isPlayable),
            isPreparedToPlay: Boolean(item?.isPreparedToPlay),
            previewURL: item?.previewURL || null,
            hasPlayParams: Boolean(item?.attributes?.playParams),
            canSupportDRM: music.player?.canSupportDRM,
            queueLength: queueItems.length,
          });

          if (isPlayingState(music, before)) {
            return true;
          }

          // Chrome Widevine attachMedia/setMediaKeys is async. Starting MSE
          // before mediaKeys exist yields blob + paused:false + time stuck at 0.
          await waitForChromeMediaKeys(music);

          // Sole start trigger: changeToMediaAtIndex autoplays (MusicKit
          // passes true). Do NOT also call play() immediately — that can
          // abort an in-flight blob load. Retry play only if still idle.
          let startResult = { timedOut: false };
          if (typeof music.changeToMediaAtIndex === 'function') {
            startResult = await withTimeout(
              music.changeToMediaAtIndex(startIndex),
              15000,
              `changeToMediaAtIndex(${startIndex},${label})`,
            );
          } else if (typeof music.player?.changeToMediaAtIndex === 'function') {
            startResult = await withTimeout(
              music.player.changeToMediaAtIndex(startIndex),
              15000,
              `player.changeToMediaAtIndex(${startIndex},${label})`,
            );
          } else {
            const playPromise =
              typeof music.play === 'function'
                ? music.play()
                : music.player?.play?.();
            if (playPromise) {
              startResult = await withTimeout(
                playPromise,
                15000,
                `play(${label})`,
              );
            }
          }

          if (startResult.error) {
            console.error('[Apple Music] start threw', startResult.error);
          } else if (!startResult.timedOut) {
            console.info(`[Apple Music] start resolved (${label})`, {
              value: startResult.value,
              state: readPlaybackState(music),
              time:
                music.currentPlaybackTime ?? music.player?.currentPlaybackTime,
              nowPlaying:
                music.nowPlayingItem?.attributes?.name ||
                music.player?.nowPlayingItem?.attributes?.name,
            });
          }

          logAppleMusicAudioEl(`after-start:${label}`);
          const audioAfter = readAppleMusicAudioEl();
          bindAppleMusicAudioDiagnostics(audioAfter);

          // Require currentTime to advance — !paused + time 0 is a silent stall.
          // MusicKit Chrome MSE can race sourceopen; retry start a couple times.
          // One patient attempt only. Restarting here made MusicKit re-download
          // the whole track per retry and fill the SourceBuffer three times.
          const started = await pollUntilPlaying(label, 50);
          if (!started) {
            const audio = logAppleMusicAudioEl(`stalled:${label}`);
            bindAppleMusicAudioDiagnostics(audio);
            const extension = getMusicKitExtension(music);
            const audioPlayer = getMusicKitAudioPlayer(music);
            console.info('[Apple Music] stall diagnostics', {
              canSupportDRM: music.player?.canSupportDRM,
              hasMediaSession: extension?.hasMediaSession,
              isWidevine: extension?.session?.isWidevine,
              keySystem: extension?.session?.keySystem || null,
              initiated: extension?.initiated,
              extURI: extension?.extURI
                ? String(extension.extURI).slice(0, 80)
                : null,
              hasMediaKeys: Boolean(
                audioPlayer?.audio?.mediaKeys || audio?.mediaKeys,
              ),
              bufferedEnd: readAudioBufferedSeconds(audio),
              readyState: audio?.readyState,
              networkState: audio?.networkState,
              duration: audio?.duration,
              volume: audio?.volume,
              audioElementCount: document.querySelectorAll('audio').length,
              previewOnly: readMusicKitPreviewOnly(music),
            });
          }

          return started;
        }

        async function playPreviewFallback(item, reason) {
          if (readHtmlAudioActive()) {
            console.warn(
              '[Apple Music] skipping preview fallback — html audio already active',
              reason,
            );
            return true;
          }
          const previewUrl = item?.attributes?.previews?.[0]?.url;
          if (!previewUrl) return false;
          console.warn('[Apple Music] falling back to preview audio', reason);
          try {
            if (typeof music.stop === 'function') {
              music.stop();
            } else if (typeof music.player?.stop === 'function') {
              music.player.stop();
            }
          } catch {
            // ignore — stop is best-effort before HTML5 preview
          }
          const audio = new Audio(previewUrl);
          audio.volume = volume;
          await audio.play();
          const durationMs = Number(item.attributes?.durationInMillis || 30000);
          setPlayback({
            is_playing: true,
            progress_ms: 0,
            shuffle_state: false,
            repeat_state: 'off',
            track: {
              id: item.id,
              name: item.attributes?.name || 'Preview',
              uri: `applemusic:song:${item.id}`,
              duration_ms: Math.min(durationMs, 30000),
              artists: item.attributes?.artistName
                ? [{ id: null, name: item.attributes.artistName }]
                : [],
              image_url: null,
              is_saved: false,
            },
            device: null,
            context_uri: `applemusic:song:${item.id}`,
          });
          toast.error(
            'Full Apple Music playback did not start. Playing a 30s preview instead.',
            { duration: 7000 },
          );
          setPlayerError(null);
          return true;
        }

        let queued = false;
        let catalogItem = null;
        let started = false;
        const localHost = isLocalDevHost();
        const previousPreviewOnly = readMusicKitPreviewOnly(music);
        let previewOnlyForced = false;

        // FairPlay full tracks hang on mkcert localhost (play() never settles).
        // Use MusicKit previewOnly locally so the real player still drives the dock.
        if (localHost) {
          previewOnlyForced = setMusicKitPreviewOnly(music, true);
          console.warn(
            '[Apple Music] localhost detected — using MusicKit previewOnly (full tracks need deployed HTTPS + APPLE_MUSIC_ORIGIN)',
          );
        }

        try {
          if (kind === 'song') {
            queued = await trySetQueue('song-fast', { song: mediaId });
          } else if (kind === 'playlist') {
            queued = await trySetQueue('playlist-fast', { playlist: mediaId });
          } else if (kind === 'album') {
            queued = await trySetQueue('album-fast', { album: mediaId });
          }

          if (queued) {
            started = await startPlaybackOnce(localHost ? 'local-preview' : 'fast');
          }

          // DRM play failed — retry MusicKit's own previewOnly path before HTML5.
          if (!started && !localHost && kind === 'song') {
            try {
              if (typeof music.stop === 'function') music.stop();
              else if (typeof music.player?.stop === 'function') music.player.stop();
            } catch {
              // best-effort
            }
            previewOnlyForced = setMusicKitPreviewOnly(music, true);
            queued = await trySetQueue('previewOnly song', { song: mediaId });
            if (queued) {
              started = await startPlaybackOnce('previewOnly');
            }
          }
        } finally {
          if (previewOnlyForced) {
            setMusicKitPreviewOnly(music, previousPreviewOnly);
            previewOnlyForced = false;
          }
        }

        if (!started && kind === 'song') {
          try {
            if (typeof music.api?.song === 'function') {
              catalogItem = await music.api.song(mediaId);
              if (Array.isArray(catalogItem)) catalogItem = catalogItem[0];
            } else if (music.api?.music) {
              const path = `/v1/catalog/${storefront}/songs/${mediaId}`;
              const response = await music.api.music(path);
              catalogItem = response?.data?.data?.[0] || null;
            }
            console.info('[Apple Music] catalog fetch', {
              ok: Boolean(catalogItem),
              id: catalogItem?.id,
              name: catalogItem?.attributes?.name,
              hasPreview: Boolean(catalogItem?.attributes?.previews?.[0]?.url),
              playParams: catalogItem?.attributes?.playParams,
            });
          } catch (error) {
            console.warn('[Apple Music] catalog fetch failed', error);
          }
        }

        if (!started && !queued && catalogItem && window.MusicKit?.MediaItem) {
          try {
            const mediaItem = new window.MusicKit.MediaItem({
              id: catalogItem.id,
              type: 'song',
              attributes: catalogItem.attributes,
              container: { id: catalogItem.id },
            });
            if (localHost) {
              previewOnlyForced = setMusicKitPreviewOnly(music, true);
            }
            queued = await trySetQueue('MediaItem', { items: [mediaItem] });
            if (queued) {
              started = await startPlaybackOnce('MediaItem');
            }
          } catch (error) {
            console.warn('[Apple Music] MediaItem queue failed', error);
          } finally {
            if (previewOnlyForced) {
              setMusicKitPreviewOnly(music, previousPreviewOnly);
              previewOnlyForced = false;
            }
          }
        }

        if (offsetUri && (kind === 'playlist' || kind === 'album') && queued && started) {
          const offset = parseAppleMusicUri(offsetUri);
          if (offset?.kind === 'song') {
            const items = musicQueue(music)?.items || [];
            const index = items.findIndex((item) => {
              const itemId =
                item?.id || item?.attributes?.playParams?.catalogId;
              return itemId === offset.id;
            });
            if (index >= 0 && typeof music.changeToMediaAtIndex === 'function') {
              await music.changeToMediaAtIndex(index);
            } else if (
              index >= 0 &&
              typeof music.player?.changeToMediaAtIndex === 'function'
            ) {
              await music.player.changeToMediaAtIndex(index);
            }
          }
        }

        console.info('[Apple Music] after play', {
          started,
          queued,
          localHost,
          playbackState: readPlaybackState(music),
          nowPlaying:
            music.nowPlayingItem?.attributes?.name ||
            music.nowPlayingItem?.id ||
            music.player?.nowPlayingItem?.attributes?.name,
          currentTime:
            music.currentPlaybackTime ?? music.player?.currentPlaybackTime,
        });

        if (started) {
          if (localHost && !localPreviewNoticeShownRef.current) {
            localPreviewNoticeShownRef.current = true;
            toast(
              'Local Apple Music plays 30s previews. Full tracks need the live HTTPS site (APPLE_MUSIC_ORIGIN must match that domain).',
              { duration: 6000 },
            );
          }
          userPlaybackIntentRef.current = 'playing';
          setPlayback((prev) => ({
            ...prev,
            context_uri:
              contextUri ||
              (kind === 'song' ? `applemusic:song:${mediaId}` : prev.context_uri),
            is_playing: true,
            track:
              trackFromNowPlayingItem(
                music.nowPlayingItem || music.player?.nowPlayingItem,
                prev.track,
              ) || prev.track,
          }));
          syncFromMusic(music);
          startProgressPoll();
          setPlayerError(null);
          return;
        }

        if (!catalogItem && kind === 'song') {
          try {
            if (typeof music.api?.song === 'function') {
              catalogItem = await music.api.song(mediaId);
              if (Array.isArray(catalogItem)) catalogItem = catalogItem[0];
            } else if (music.api?.music) {
              const path = `/v1/catalog/${storefront}/songs/${mediaId}`;
              const response = await music.api.music(path);
              catalogItem = response?.data?.data?.[0] || null;
            }
          } catch (error) {
            console.warn('[Apple Music] late catalog fetch failed', error);
          }
        }
        if (
          catalogItem &&
          (await playPreviewFallback(catalogItem, 'play did not start'))
        ) {
          return;
        }
        throw new Error(
          queued
            ? 'Apple Music queued the track but playback did not start.'
            : 'Apple Music could not queue the track.',
        );
      });
    },
    [connection?.storefront, ensureAuthorizedMusic, runControl, startProgressPoll, syncFromMusic, volume],
  );

  const playPlaylist = useCallback(
    (playlist) => {
      const uri = playlistUri(playlist);
      if (!uri) {
        return Promise.reject(new Error('Missing playlist'));
      }
      return playContext({
        contextUri: uri,
        externalUrl: playlist.external_url || playlist.externalUrl || null,
      });
    },
    [playContext],
  );

  const playTrack = useCallback(
    (track, context = null) => {
      if (context?.uri || context?.id) {
        return playContext({
          contextUri: playlistUri(context),
          offsetUri: songUri(track),
          externalUrl: track?.external_url || track?.externalUrl || null,
        });
      }
      const uri = songUri(track);
      if (!uri) {
        return Promise.reject(new Error('Missing track'));
      }
      return playContext({
        uris: [uri],
        externalUrl: track?.external_url || track?.externalUrl || null,
      });
    },
    [playContext],
  );

  const pause = useCallback(() => {
    return runControl(async () => {
      const music = musicRef.current;
      if (!music) return;
      userPlaybackIntentRef.current = 'paused';
      stopProgressPoll();
      setPlayback((prev) => ({ ...prev, is_playing: false }));
      try {
        if (typeof music.pause === 'function') {
          await music.pause();
        } else if (typeof music.player?.pause === 'function') {
          await music.player.pause();
        }
      } catch (error) {
        userPlaybackIntentRef.current = 'playing';
        setPlayback((prev) => ({ ...prev, is_playing: true }));
        startProgressPoll();
        throw error;
      }
    });
  }, [runControl, startProgressPoll, stopProgressPoll]);

  const resume = useCallback(() => {
    return runControl(async () => {
      const music = await ensureAuthorizedMusic();
      userPlaybackIntentRef.current = 'playing';
      setPlayback((prev) => ({ ...prev, is_playing: true }));
      try {
        await musicPlay(music);
        startProgressPoll();
      } catch (error) {
        userPlaybackIntentRef.current = 'paused';
        setPlayback((prev) => ({ ...prev, is_playing: false }));
        stopProgressPoll();
        throw error;
      }
    });
  }, [ensureAuthorizedMusic, runControl, startProgressPoll, stopProgressPoll]);

  const togglePlay = useCallback(() => {
    if (playback.is_playing) return pause();
    if (playback.track) return resume();
    return Promise.resolve();
  }, [pause, playback.is_playing, playback.track, resume]);

  const next = useCallback(() => {
    return runControl(async () => {
      const music = await ensureAuthorizedMusic();
      if (typeof music.skipToNextItem === 'function') {
        await music.skipToNextItem();
      } else if (typeof music.player?.skipToNextItem === 'function') {
        await music.player.skipToNextItem();
      }
    });
  }, [ensureAuthorizedMusic, runControl]);

  const previous = useCallback(() => {
    return runControl(async () => {
      const music = await ensureAuthorizedMusic();
      if (typeof music.skipToPreviousItem === 'function') {
        await music.skipToPreviousItem();
      } else if (typeof music.player?.skipToPreviousItem === 'function') {
        await music.player.skipToPreviousItem();
      }
    });
  }, [ensureAuthorizedMusic, runControl]);

  const seek = useCallback(
    (positionMs) => {
      return runControl(async () => {
        const music = await ensureAuthorizedMusic();
        const seconds = Math.max(0, Number(positionMs || 0) / 1000);
        if (typeof music.seekToTime === 'function') {
          await music.seekToTime(seconds);
        } else if (typeof music.player?.seekToTime === 'function') {
          await music.player.seekToTime(seconds);
        }
        setPlayback((prev) => ({
          ...prev,
          progress_ms: Math.round(seconds * 1000),
        }));
      });
    },
    [ensureAuthorizedMusic, runControl],
  );

  const setShuffle = useCallback(
    (state) => {
      return runControl(async () => {
        const music = await ensureAuthorizedMusic();
        const ShuffleMode =
          music.ShuffleMode || window.MusicKit?.ShuffleMode || {};
        music.shuffleMode = state
          ? ShuffleMode.SONGS ?? 1
          : ShuffleMode.OFF ?? 0;
        setPlayback((prev) => ({
          ...prev,
          shuffle_state: Boolean(state),
        }));
      });
    },
    [ensureAuthorizedMusic, runControl],
  );

  const setRepeat = useCallback(
    (state) => {
      return runControl(async () => {
        const music = await ensureAuthorizedMusic();
        music.repeatMode = musicKitRepeatMode(music, state);
        setPlayback((prev) => ({
          ...prev,
          repeat_state: state || 'off',
        }));
      });
    },
    [ensureAuthorizedMusic, runControl],
  );

  const transferTo = useCallback(() => Promise.resolve(null), []);

  const refreshDevices = useCallback(() => Promise.resolve([]), []);

  const refreshStatus = useCallback(() => {
    syncFromMusic(musicRef.current);
    return Promise.resolve(playback);
  }, [playback, syncFromMusic]);

  const toggleSave = useCallback(() => {
    const track = playback.track;
    if (!track?.id) return Promise.resolve();
    const nextSaved = !track.is_saved;
    setPlayback((prev) => {
      if (!prev.track || prev.track.id !== track.id) return prev;
      return {
        ...prev,
        track: {
          ...prev.track,
          is_saved: nextSaved,
        },
      };
    });
    return runControl(() => {
      if (track.is_saved) {
        return removeAppleMusicTrack(track.id);
      }
      return saveAppleMusicTrack(track.id);
    }).catch((error) => {
      setPlayback((prev) => {
        if (!prev.track || prev.track.id !== track.id) return prev;
        return {
          ...prev,
          track: {
            ...prev.track,
            is_saved: track.is_saved,
          },
        };
      });
      throw error;
    });
  }, [playback.track, runControl]);

  const setVolume = useCallback((nextVolume) => {
    const clamped = Math.max(0, Math.min(1, Number(nextVolume)));
    if (Number.isNaN(clamped)) return Promise.resolve(null);
    setVolumeState(clamped);
    const music = musicRef.current;
    if (music && typeof music.volume === 'number') {
      music.volume = clamped;
    }
    return Promise.resolve(clamped);
  }, []);

  useEffect(() => {
    refreshConnection()
      .then((payload) => {
        if (payload?.connected) {
          return ensureConfigured()
            .then((music) => {
              setAuthorized(Boolean(music?.isAuthorized));
              setReady(Boolean(music));
              if (music?.isAuthorized) {
                syncFromMusic(music);
              }
            })
            .catch(() => null);
        }
        return null;
      })
      .catch(() => null);
  }, [ensureConfigured, refreshConnection, syncFromMusic]);

  useEffect(() => {
    if (!connected) {
      setReady(false);
      setAuthorized(false);
    }
  }, [connected]);

  const value = useMemo(
    () => ({
      connected,
      connection,
      ready: ready && (authorized || !connected),
      // `ready` folds in authorization, so it cannot distinguish "still booting
      // MusicKit" from "booted but this browser is not authorized". Callers
      // gating an unauthorized screen need that difference.
      configured: ready,
      authorized,
      deviceId: null,
      playback,
      devices: [],
      playerError,
      busy,
      nowPlayingOpen,
      setNowPlayingOpen,
      headerPlayerMounted,
      registerHeaderPlayer,
      refreshConnection,
      refreshStatus,
      refreshDevices,
      authorizeAndConnect,
      disconnectAndForget,
      playContext,
      playPlaylist,
      playTrack,
      pause,
      resume,
      togglePlay,
      next,
      previous,
      seek,
      setShuffle,
      setRepeat,
      setVolume,
      transferTo,
      toggleSave,
      volume,
    }),
    [
      authorizeAndConnect,
      authorized,
      busy,
      connected,
      connection,
      disconnectAndForget,
      headerPlayerMounted,
      next,
      nowPlayingOpen,
      pause,
      playContext,
      playPlaylist,
      playTrack,
      playback,
      playerError,
      previous,
      ready,
      refreshConnection,
      refreshDevices,
      refreshStatus,
      registerHeaderPlayer,
      resume,
      seek,
      setRepeat,
      setShuffle,
      setVolume,
      togglePlay,
      toggleSave,
      transferTo,
      volume,
    ],
  );

  return (
    <AppleMusicPlayerContext.Provider value={value}>
      {children}
    </AppleMusicPlayerContext.Provider>
  );
}
